import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, of } from 'rxjs';
import {
  catchError, filter, map, switchMap, take,
} from 'rxjs/operators';
import { toZfsSnapshot } from 'app/interfaces/zfs-snapshot.interface';
import { WebUiQueryEntity } from 'app/modules/websocket/typed-api/typed-api-client.token';
import { TypedApiService } from 'app/modules/websocket/typed-api/typed-api.service';
import {
  snapshotAdded, snapshotChanged,
  snapshotPageEntered,
  snapshotRemoved, snapshotsLoaded, snapshotsNotLoaded,
} from 'app/pages/datasets/modules/snapshots/store/snapshot.actions';
import { AppState } from 'app/store';
import { waitForPreferences } from 'app/store/preferences/preferences.selectors';

@Injectable()
export class SnapshotEffects {
  private actions$ = inject(Actions);
  private api = inject(TypedApiService);
  private store$ = inject<Store<AppState>>(Store);
  private translate = inject(TranslateService);

  loadSnapshots$ = createEffect(() => this.actions$.pipe(
    ofType(snapshotPageEntered),
    switchMap(() => this.store$.pipe(waitForPreferences, take(1))),
    switchMap((preferences) => {
      const showExtraColumns = preferences.showSnapshotExtraColumns;
      const extraColumns: (keyof WebUiQueryEntity<'pool.snapshot.query'>)[] = showExtraColumns ? ['properties'] : [];
      return this.api.query('pool.snapshot.query', [], {
        select: ['snapshot_name', 'dataset', 'name', ...extraColumns],
        order_by: ['name'],
        ...(showExtraColumns ? { extra: { properties: ['creation', 'used', 'referenced'] } } : {}),
      }).pipe(
        map((snapshots) => snapshotsLoaded({ snapshots: snapshots.map(toZfsSnapshot) })),
        catchError((error: unknown) => {
          console.error(error);
          // TODO: See if it would make sense to parse middleware error.
          return of(snapshotsNotLoaded({
            error: this.translate.instant('Snapshots could not be loaded'),
          }));
        }),
      );
    }),
  ));

  subscribeToUpdates$ = createEffect(() => this.actions$.pipe(
    ofType(snapshotsLoaded),
    switchMap(() => {
      return this.api.subscribe('pool.snapshot.query').pipe(
        filter((event) => event.msg !== 'removed'),
        switchMap((event) => {
          switch (event.msg) {
            case 'added':
              return of(snapshotAdded({ snapshot: toZfsSnapshot(event.fields) }));
            case 'changed':
              return of(snapshotChanged({ snapshot: toZfsSnapshot(event.fields) }));
            default:
              return EMPTY;
          }
        }),
      );
    }),
  ));

  subscribeToRemoval$ = createEffect(() => this.actions$.pipe(
    ofType(snapshotsLoaded),
    switchMap(() => {
      return this.api.subscribe('pool.snapshot.query').pipe(
        filter((event) => event.msg === 'removed'),
        map((event) => snapshotRemoved({ id: event.id.toString() })),
      );
    }),
  ));
}
